import styled from 'styled-components';

import { HeroContent } from '../../components/HubPages/components/HeroImage/HeroContent';
import { ImageCardList } from '../../components/ImageCard/ImageCardList';
import { Editorial } from '../../components/HubPages/components/Editorial/Editorial';
import { Lookup } from '../../components/HubPages/components/Lookup/Lookup';
import { SectionBreakHeader } from '../../components/HubPages/components/SectionBreakHeader/SectionBreakHeader';
import { Testimonial } from '../../components/HubPages/components/Testimonial/Testimonial';
import { media } from '../../helpers/breakpoints';

import type {
  HubPageResponse,
  Body,
  Item,
  Primary,
  ContentLinkComponent,
  EditorialContentComponent,
  ListComponent,
  ParagraphComponent,
  TestimonialComponent,
} from '../../api/types/hubpageApiTypes';
import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';

interface FormattedPrismicValues {
  componentName: string;
  props: {
    listItems?: Item[];
    props?: Primary;
  };
  grouped: boolean;
}

export const PageWrapper = styled.div`
  margin: 0 auto;
`;

export const HeroImage = styled.div<{
  heroImage: string;
}>`
  position: absolute;
  top: 0;
  width: 100%;

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.48), rgba(0, 0, 0, 0.48)),
    url('${({ heroImage }) => `${heroImage}`}') center center no-repeat;
  background-size: cover;
  height: 360px;
  width: 100%;

  ${media.large} {
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.48), rgba(0, 0, 0, 0.48)),
      url('${({ heroImage }) => `${heroImage}`}') center no-repeat;
    background-size: cover;
    height: 400px;
  }
`;

const ContentWrapper = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing.S8}`};
  margin-bottom: ${({ theme }) => theme.spacing.L40};
  width: 100%;

  ${media.large} {
    padding: 0;
  }
`;

const Container = styled.div`
  ${media.large} {
    max-width: 760px;
  }
`;

enum COMPONENT_NAME {
  CONTENT_LINK = 'content_link_component',
  EDITORIAL = 'editorial_content_component',
  LIST = 'list_component',
  LOOKUP = 'lookup_component',
  PARAGRAPH = 'paragraph',
  HEADER = 'section_break_header_component',
  TESTIMONIAL = 'testimonial_component',
}

function displayHubPageComponents(
  key: number,
  componentName: string,
  props?: Primary,
  listItems?: Item[],
) {
  switch (componentName) {
    case COMPONENT_NAME.CONTENT_LINK:
      return (
        <ImageCardList
          key={key}
          items={(listItems ?? []).map((item: ContentLinkComponent) => ({
            image: item.content_link_image.url,
            title: item.content_link_subtitle,
            subtext: item.content_link_subtext,
            linkText: item.content_link_text,
            link: item.content_link.url,
          }))}
        />
      );
    case COMPONENT_NAME.EDITORIAL:
      return (
        <div key={key}>
          {listItems?.map((item: EditorialContentComponent, index: number) => {
            return (
              <Editorial
                key={index}
                title={item.article_title}
                content={item.article_description}
                linkText={item.article_link_text}
                link={item?.article_link?.url}
                image={item?.article_image?.url}
                sponsored={item.sponsored_post}
              />
            );
          })}
        </div>
      );

    case COMPONENT_NAME.LIST:
      // {props && listItems && ()}
      const list = listItems?.map((item: ListComponent) => {
        return {
          icon: props?.list_item_icon?.url,
          title: item.list_item_title,
          body: item.list_item_description,
        };
      });

      return (
        <div key={key}>
          {props && (
            <Editorial
              title={props.list_title}
              content={props.list_description}
              linkText={props.list_link_text}
              link={''}
              list={list}
            />
          )}
        </div>
      );
    case COMPONENT_NAME.LOOKUP:
      return (
        <div key={key}>
          {props && (
            <Lookup
              key={key}
              title={props.lookup_title}
              description={props.lookup_description}
              inputIcon={props.lookup_field_icon.url}
              inputIconColour={props.lookup_field_icon_background_colour}
              inputPlaceHolderText={props.lookup_field_placeholder_text}
              ctaText={props.lookup_cta}
              ctaLink={props.lookup_cta_link}
            />
          )}
        </div>
      );
    case COMPONENT_NAME.PARAGRAPH:
      return listItems?.map((item: ParagraphComponent, index: number) => {
        return (
          <div key={key}>
            <Editorial
              key={index}
              title={item.paragraph_subtitle}
              content={item.paragraph_content}
              linkText={item.paragraph_link_text}
              link={item.paragraph_link?.url}
            />
          </div>
        );
      });
    case COMPONENT_NAME.HEADER:
      return (
        <SectionBreakHeader
          title={props ? props.section_break_header : ''}
          key={key}
        />
      );
    case COMPONENT_NAME.TESTIMONIAL:
      return (
        <div key={key}>
          {listItems?.map((item: TestimonialComponent, index: number) => {
            return (
              <Testimonial
                key={index}
                icon={item.attestant_icon.url}
                name={item.attestant_name}
                description={item.attestant_description}
                testimonial={item.attestant_testimonial}
              />
            );
          })}
        </div>
      );
    default:
      return null;
  }
}

function mapPrismicValues(body: Body[]) {
  return body.map((slice: Body) => {
    let primaryProps: Primary | {} = {};
    let listItems: Item[] = [];
    let props = {};

    if (typeSafeObjectKeys(slice.primary).length > 0) {
      primaryProps = slice.primary;
    }

    let grouped = false;
    if (slice.items[0]) {
      grouped = Boolean(typeSafeObjectKeys(slice.items[0]).length > 0);
    }

    if (grouped) {
      listItems = slice.items.map((item) => item);
    }

    if (primaryProps && listItems) {
      props = { props: primaryProps, listItems };
    } else if (listItems) {
      props = { listItems };
    } else {
      props = { props: primaryProps };
    }

    return {
      componentName: slice.slice_type,
      props,
      grouped,
    };
  });
}

function HubPage(props: HubPageResponse) {
  const {
    body,
    hero_cta,
    hero_cta_link,
    hero_info_text,
    hero_title,
    hero_image,
    hero_info_text_link,
    hero_sponsored_text,
    hero_sponsored_image,
    title_image,
    valuation_component_toggle,
    reg_lookup_component_toggle,
    history_check_items_title,
    history_check_items,
    valuation_type,
    cdnUrl,
    domain,
  } = props;

  const slices: FormattedPrismicValues[] = mapPrismicValues(body);

  return (
    <PageWrapper>
      <HeroImage heroImage={hero_image.url.replace(`h=400`, 'h=360')} />

      <HeroContent
        heroTitle={hero_title}
        ctaText={hero_cta}
        ctaLink={hero_cta_link}
        infoText={hero_info_text}
        infoTextLink={hero_info_text_link?.url}
        heroSponsoredText={hero_sponsored_text}
        heroSponsoredImage={hero_sponsored_image}
        titleImage={title_image}
        showValuationComponent={valuation_component_toggle}
        showRegLookup={reg_lookup_component_toggle}
        historyCheckItemsTitle={history_check_items_title?.[0]}
        historyCheckItems={history_check_items}
        valuationLookupDestinationPage={valuation_type}
        cdnUrl={cdnUrl}
        baseUrl={domain}
      />

      <Container className="row set-width-100">
        <ContentWrapper>
          {slices.map((slice, index) => {
            const { componentName } = slice;
            const { props, listItems } = slice.props;

            return displayHubPageComponents(
              index,
              componentName,
              props,
              listItems,
            );
          })}
        </ContentWrapper>
      </Container>
    </PageWrapper>
  );
}

export default HubPage;
